import { ActionIcon, Button, Group, Modal, Stack, Text, Tooltip } from "@mantine/core";
import { Link, useMatchRoute, useParams } from "@tanstack/react-router";
import { ArrowRight, Gift, Palette, Sparkles, Volume2, VolumeX } from "lucide-react";
import { useMemo, useRef, useState, type CSSProperties } from "react";

import { useListMyTeams } from "../../api/generated/endpoints/teams/teams";
import type { AppearanceView, CaseResult } from "../../api/generated/model";
import { ErrorAlert } from "../ErrorAlert";
import { readPref, writePref } from "../prefs";
import { useThemeChoice } from "../themes/account";
import { useThemeFamily } from "../themes/current";
import { familyById } from "../themes/families";
import classes from "./case.module.css";
import { Celebration, type Landing } from "./Celebration";
import { jackpotCredits, percent, prizeLabel, reelOdds, resultCard, useOpenCase } from "./daily-case";
import { CaseReel } from "./CaseReel";
import { buildStrip, fillerCard, type ReelCard } from "./reel";
import { chime, tick, unlock } from "./sound";
import { ThemeCase } from "./ThemeCase";

/**
 * What the case gave, in words. The jackpot names the team it paid: today's result is the same in
 * every team, but the credits are in the team where the case was opened (spec web/case-opening,
 * decision 9).
 */
function Won({ result, teamId }: { result: CaseResult; teamId: string }) {
  const paid = useListMyTeams().data?.find((team) => team.id === result.team_id)?.name;
  const rarity = { "--rarity": `var(--cai-rarity-${result.prize})` } as CSSProperties;
  const tag = (
    <span className={classes.tag} style={rarity}>
      {prizeLabel(result.prize)}
    </span>
  );
  if (result.theme === null) {
    return (
      <Text className={classes.won} style={rarity}>
        <b>Jackpot!</b> {(result.credits ?? 0).toLocaleString("en-US")} free credits are in{" "}
        {result.team_id === teamId
          ? "this team's balance"
          : paid === undefined
            ? "the balance of the team where today's case was opened"
            : `${paid}'s balance`}
        . {tag}
      </Text>
    );
  }
  return (
    <Text className={classes.won} style={rarity}>
      You unlocked <b>{familyById(result.theme).name}</b> {tag}
    </Text>
  );
}

/** The case's call to act, in Created.ai's purple and pink (spec web/case-polish). */
const BRAND = { from: "var(--cai-case-from)", to: "var(--cai-case-pink)", deg: 135 };

/**
 * Where the themes are (spec web/case-polish): a chip in sight from the first look, which opens the
 * Account page's Appearance tab and closes the dialog. While the case opens or the reel turns it is
 * quiet words, as the dialog offers no way out then (spec web/case-themes-link, decision 3).
 */
function ThemesChip({ teamId, busy, onFollow }: { teamId: string; busy: boolean; onFollow: () => void }) {
  if (busy) {
    return (
      <span className={classes.themesChip}>
        <Palette size={14} aria-hidden />
        Your themes
      </span>
    );
  }
  return (
    <Tooltip label="Account, Appearance">
      <Button
        onClick={onFollow}
        variant="light"
        size="compact-sm"
        radius="xl"
        leftSection={<Palette size={14} aria-hidden />}
        rightSection={<ArrowRight size={14} aria-hidden />}
        renderRoot={(rootProps) => <Link {...rootProps} to="/teams/$teamId/account/appearance" params={{ teamId }} />}
      >
        Your themes
      </Button>
    </Tooltip>
  );
}

/**
 * The free daily case (spec web/case-opening): its odds, the reel, and what it gave. The server
 * draws and saves the prize when "Open case" is pressed; the reel then turns to it, and the landing
 * celebrates by its rarity. While today's case waits, the dialog shows the Theme Case, which opens
 * as the server draws, and the reel comes out of it (spec web/case-art). Opened today already, its
 * reel rests on today's prize (spec web/case-reopen-prize). The sound is on until muted (the
 * `caseSound` pref).
 */
export function CaseDialog({
  teamId,
  view,
  opened,
  onClose,
}: {
  teamId: string;
  view: AppearanceView;
  opened: boolean;
  onClose: () => void;
}) {
  const odds = useMemo(() => reelOdds(view), [view]);
  const today = view.case.result;
  // The reel at rest: on today's prize once it is drawn, under the marker; filler before (spec
  // web/case-reopen-prize).
  const resting = useMemo<ReelCard[]>(
    () =>
      today === null
        ? Array.from({ length: 9 }, () => fillerCard(odds)).filter((card) => card !== undefined)
        : buildStrip(resultCard(today), odds),
    [odds, today],
  );
  // A spin's strip, shown from its start until the dialog closes.
  const [cards, setCards] = useState<ReelCard[]>([]);
  const [spinKey, setSpinKey] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const [skipped, setSkipped] = useState(false);
  const [landing, setLanding] = useState<Landing | null>(null);
  const [sound, setSound] = useState(() => readPref("caseSound"));
  // The Theme Case opens while the server draws, and the reel comes out once both are done (spec
  // web/case-art, decision 2); the case tells its opening's end through `caseOpened`.
  const [opening, setOpening] = useState(false);
  const [caseOpen, setCaseOpen] = useState(false);
  const [fromCase, setFromCase] = useState(false);
  const caseOpened = useRef<(() => void) | null>(null);
  // Each press of "Open case" is a run, and closing the dialog ends it: a draw that answers later
  // keeps its prize but turns no reel in a dialog closed meanwhile (spec web/case-art, decision 7).
  const run = useRef(0);
  const content = useRef<HTMLDivElement>(null);
  const open = useOpenCase();
  // "Use it now" saves the account's theme; a refused save is put back and said here.
  const { choose, update: themeSave } = useThemeChoice();
  const shown = useThemeFamily().id;
  // With no spin since the dialog opened, the reel stands on today's prize, if there is one.
  const standing = spinKey === 0 && today !== null;
  // The Appearance tab is where the themes are: there, the line that points to it is left out (spec
  // web/case-themes-link, decision 4).
  const matchRoute = useMatchRoute();
  // Pro's Settings has the same tab (spec ui-perf/pro-fixes-notfound-case): the brand is the URL's.
  const { brandId } = useParams({ strict: false });
  const onAppearance =
    matchRoute({ to: "/teams/$teamId/account/appearance", params: { teamId } }) !== false ||
    (brandId !== undefined &&
      matchRoute({ to: "/teams/$teamId/brands/$brandId/pro/settings/appearance", params: { teamId, brandId } }) !==
        false);
  // The odds say what the jackpot pays; a jackpot drawn says it too.
  const jackpot = jackpotCredits(view) ?? today?.credits ?? null;
  // Nothing is said of the prize until the reel stops, nor while the case opens: the draw's answer
  // is in the read already then (spec web/case-art, decision 3).
  const result = opening || (landing === null && spinning) ? null : today;
  // The case stands where the reel will, until it is opened.
  const showCase = opening || (spinKey === 0 && view.case.status === "waiting");
  // While the case's beats play or the reel turns, the dialog offers no way out. A draw still on its
  // way after the beats (a slow server, or offline) leaves it free to close (spec web/case-art,
  // decision 7).
  const busy = spinning || (opening && !caseOpen);
  // Offline, the draw waits for the connection (TanStack pauses it), and the footer says so.
  const openingWords = open.isPaused ? "Waiting for the connection…" : "Opening…";

  const start = async () => {
    run.current += 1;
    const mine = run.current;
    setLanding(null);
    setCaseOpen(false);
    const caseDone = new Promise<void>((done) => {
      caseOpened.current = done;
    });
    setOpening(true);
    let drawn: CaseResult;
    try {
      drawn = await open.mutateAsync({ teamId });
    } catch {
      // A refused draw closes the case again; the error is said below (spec web/case-art, decision 6).
      if (run.current === mine) setOpening(false);
      return;
    }
    await caseDone;
    if (run.current !== mine) return;
    setCards(buildStrip(resultCard(drawn), odds));
    setOpening(false);
    setFromCase(true);
    setSkipped(false);
    setSpinning(true);
    setSpinKey((key) => key + 1);
  };

  const landed = (winner: DOMRect | undefined) => {
    setSpinning(false);
    const drawn = open.data;
    if (drawn === undefined) return;
    if (readPref("caseSound")) chime(drawn.prize);
    if (winner !== undefined) setLanding({ tier: drawn.prize, at: winner });
  };

  // A closed dialog keeps no draw: reopened, its reel rests on today's prize and never turns again
  // (spec web/case-opening, decision 10; web/case-reopen-prize).
  const close = () => {
    run.current += 1;
    setSpinKey(0);
    setSkipped(false);
    setLanding(null);
    setOpening(false);
    setCaseOpen(false);
    setFromCase(false);
    open.reset();
    themeSave.reset();
    onClose();
  };

  const toggleSound = () => {
    writePref("caseSound", !sound);
    setSound(!sound);
  };

  return (
    <Modal
      opened={opened}
      onClose={close}
      // The case's beats play and the reel turns to a prize the server has saved: Skip, not a close,
      // ends a spin early.
      withCloseButton={!busy}
      closeOnEscape={!busy}
      closeOnClickOutside={!busy}
      size={880}
      centered
      title={
        <span className={classes.title}>
          <Gift size={18} aria-hidden />
          Free daily case
        </span>
      }
    >
      <div ref={content}>
        <Stack gap="md">
          {/* The line and its chips share a row where they fit; on a phone the line takes the
              row and the chips go under it, rather than squeeze it into a column. */}
          <Group justify="space-between" align="center" className={classes.aboutRow}>
            <Text className={classes.about}>
              One free case a day: nothing to buy. The server picks what you win; the reel shows it.
            </Text>
            <Group gap={6} wrap="nowrap">
              {/* On the Appearance tab the themes are in sight already (spec web/case-themes-link, decision 4). */}
              {onAppearance ? null : <ThemesChip teamId={teamId} busy={busy} onFollow={close} />}
              <Tooltip label={sound ? "Mute the case" : "Sound on"}>
                <ActionIcon variant="subtle" aria-label={sound ? "Mute the case" : "Sound on"} aria-pressed={!sound} onClick={toggleSound}>
                  {sound ? <Volume2 size={18} /> : <VolumeX size={18} />}
                </ActionIcon>
              </Tooltip>
            </Group>
          </Group>
          <ul className={classes.odds} aria-label="Odds">
            {view.case.odds.map(({ prize, basis_points, credits }) => (
              <li key={prize} style={{ "--rarity": `var(--cai-rarity-${prize})` } as CSSProperties}>
                {prizeLabel(prize)} {percent(basis_points)}
                {credits === null ? null : `, ${credits.toLocaleString("en-US")} credits`}
              </li>
            ))}
          </ul>
          {/* The dark showcase stage the case and the reel stand on (spec web/case-polish). */}
          <div className={classes.stage} data-stage>
            {showCase ? (
              <ThemeCase
                opening={opening}
                onLid={() => {
                  if (readPref("caseSound")) unlock();
                }}
                onOpened={() => {
                  setCaseOpen(true);
                  caseOpened.current?.();
                }}
              />
            ) : (
              // Out of the opened case, the reel opens from the middle (spec web/case-art, decision 4).
              // `data-landed` once a spin has stopped, or at rest on today's prize: then the prize glows
              // (spec web/case-polish).
              <div
                className={fromCase ? classes.reelOut : undefined}
                data-landed={(spinKey > 0 && !spinning) || standing ? "" : undefined}
              >
                <CaseReel
                  cards={spinKey === 0 ? resting : cards}
                  jackpot={jackpot}
                  spinKey={spinKey}
                  standing={standing}
                  skipped={skipped}
                  onTick={() => {
                    if (readPref("caseSound")) tick();
                  }}
                  onLanded={landed}
                />
              </div>
            )}
          </div>
          {open.isError ? <ErrorAlert error={open.error} /> : null}
          {themeSave.isError ? <ErrorAlert error={themeSave.error} /> : null}
          <output className={classes.reveal} aria-live="polite">
            {result === null ? null : <Won result={result} teamId={teamId} />}
          </output>
          <Group justify="space-between">
            <Text size="sm" c="var(--cai-color-text-muted)">
              {opening
                ? openingWords
                : view.case.status === "waiting" && !spinning
                  ? "1 free case left today"
                  : "Next free case tomorrow"}
            </Text>
            {opening ? null : view.case.status === "waiting" && !spinning ? (
              <Button
                variant="gradient"
                gradient={BRAND}
                size="md"
                leftSection={<Sparkles size={16} aria-hidden />}
                onClick={() => void start()}
                loading={open.isPending}
              >
                Open case
              </Button>
            ) : spinning ? (
              // The footer's buttons share one size, so the dialog keeps its height through a spin.
              <Button variant="outline" size="md" onClick={() => setSkipped(true)}>
                Skip
              </Button>
            ) : result?.theme !== null && result?.theme !== undefined && result.theme !== shown ? (
              <Button
                variant="gradient"
                gradient={BRAND}
                size="md"
                onClick={() => choose(result.theme as NonNullable<CaseResult["theme"]>)}
              >
                Use it now
              </Button>
            ) : null}
          </Group>
        </Stack>
      </div>
      {landing === null ? null : <Celebration landing={landing} shaken={content} />}
    </Modal>
  );
}
